import CryptoJS from 'crypto-js';

import Configs from '@/Configs';

/** List + profile base paths (must match `pulseHeader` tab URLs for active-state prefix match). */
export const PULSE_DRIVERS_SECTION_PATH = '/pulse/drivers';
export const PULSE_QUESTIONS_SECTION_PATH = '/pulse/questions';
export const PULSE_COMMENTS_SECTION_PATH = '/pulse/comments';

const LEGACY_DRIVER_PROFILE_PATH = /^\/pulse\/driver\/(.+)$/;
const LEGACY_QUESTION_PROFILE_PATH = /^\/pulse\/question\/([^/]+)$/;

export type PulseDriverProfileParams = {
  driver: string;
};

/**
 * Builds the encrypted URL segment for `/pulse/drivers/:id`.
 *
 * @param {string} driverLabel
 * @returns {string}
 */
export function buildPulseDriverProfileEncryptedId(driverLabel: string): string {
  return encodeURIComponent(
    CryptoJS.AES.encrypt(JSON.stringify({ driver: driverLabel || '' }), Configs.privateKey).toString()
  );
}

/**
 * @param {string} driverLabel
 * @returns {string}
 */
export function buildPulseDriverProfilePath(driverLabel: string): string {
  return `${PULSE_DRIVERS_SECTION_PATH}/${buildPulseDriverProfileEncryptedId(driverLabel)}`;
}

/**
 * @param {string} questionID
 * @returns {string}
 */
export function buildPulseQuestionProfilePath(questionID: string): string {
  return `${PULSE_QUESTIONS_SECTION_PATH}/${questionID}`;
}

/**
 * Decrypts the `:id` segment from `/pulse/drivers/:id`.
 *
 * @param {string | undefined} encryptedId
 * @returns {PulseDriverProfileParams | null}
 */
export function parsePulseDriverProfileId(
  encryptedId: string | undefined
): PulseDriverProfileParams | null {
  if (!encryptedId) {
    return null;
  }

  try {
    const bytes = CryptoJS.AES.decrypt(encryptedId, Configs.privateKey);
    const decryptedString = bytes.toString(CryptoJS.enc.Utf8);
    if (!decryptedString) {
      return null;
    }

    return JSON.parse(decryptedString) as PulseDriverProfileParams;
  } catch {
    return null;
  }
}

/**
 * Maps legacy singular profile URLs to the plural section-nested paths.
 *
 * @param {string} pathname
 * @returns {string | null} Redirect target, or null when pathname is not a legacy profile URL.
 */
export function resolveLegacyPulseProfilePath(pathname: string): string | null {
  const legacyQuestionMatch = pathname.match(LEGACY_QUESTION_PROFILE_PATH);
  if (legacyQuestionMatch) {
    return buildPulseQuestionProfilePath(legacyQuestionMatch[1]);
  }

  const legacyDriverMatch = pathname.match(LEGACY_DRIVER_PROFILE_PATH);
  if (legacyDriverMatch) {
    return `${PULSE_DRIVERS_SECTION_PATH}/${legacyDriverMatch[1]}`;
  }

  return null;
}

/**
 * Mirrors React Router `NavLink` prefix matching used by `ModuleHeader` (no `end` prop).
 *
 * @param {string} pathname
 * @param {string} sectionUrl
 * @returns {boolean}
 */
export function isPulseHeaderSectionActive(pathname: string, sectionUrl: string): boolean {
  return pathname === sectionUrl || pathname.startsWith(`${sectionUrl}/`);
}
